// src/components/PlayerInstructions.js
import React from "react";
import { 
  Box, 
  Typography, 
  Button, 
  Paper, 
  Alert, 
  List, 
  ListItem, 
  ListItemText,
  Link 
} from "@mui/material";
import OpenInNewIcon from "@mui/icons-material/OpenInNew";
import ExtensionIcon from "@mui/icons-material/Extension";

const TAMPERMONKEY_SCRIPT_URL = "/scripts/karaoke-queue.user.js";

export default function PlayerInstructions() {
  const handleInstallScript = () => {
    window.open(TAMPERMONKEY_SCRIPT_URL, "_blank");
  };

  return (
    <Box sx={{ 
      p: 3, 
      height: "calc(100vh - 120px)",   // Leave space for header
      overflow: "hidden",
      display: "flex",
      flexDirection: "column"
    }}>
      <Typography variant="h4" gutterBottom align="center">
        Karaoke Player Setup
      </Typography>

      <Alert severity="info" sx={{ mb: 3 }}>
        The actual video playback and queue control is handled by a Tampermonkey script that runs on YouTube.
      </Alert>

      {/* Scrollable Content Area */}
      <Box sx={{ 
        flex: 1, 
        overflowY: "auto", 
        pr: 1, 
        '&::-webkit-scrollbar': {
          width: '8px',
        },
        '&::-webkit-scrollbar-thumb': {
          backgroundColor: '#888',
          borderRadius: '4px',
        }
      }}>
        <Paper sx={{ p: 3, mb: 3 }}>
          <Typography variant="h6" gutterBottom>
            Required Browser Extensions
          </Typography>

          <List>
            <ListItem>
              <ListItemText 
                primary={
                  <Link 
                    href="https://chromewebstore.google.com/detail/dhdgffkkebhmkfjojejmpbldmpobfkfo"
                    target="_blank"
                    rel="noopener"
                    sx={{ display: 'flex', alignItems: 'center', gap: 1 }}
                  >
                    <ExtensionIcon /> Tampermonkey
                  </Link>
                }
                secondary="Required to run the karaoke queue script on YouTube"
              />
            </ListItem>

            <ListItem>
              <ListItemText 
                primary={
                  <Link 
                    href="https://chromewebstore.google.com/detail/ioimlbgefgadofblnajllknopjboejda"
                    target="_blank"
                    rel="noopener"
                    sx={{ display: 'flex', alignItems: 'center', gap: 1 }}
                  >
                    <ExtensionIcon /> Transpose
                  </Link>
                }
                secondary="Allows you to change the key of the song being played (highly recommended for karaoke)"
              />
            </ListItem>
          </List>
        </Paper>

        <Paper sx={{ p: 3, mb: 3 }}>
          <Typography variant="h6" gutterBottom>
            Installation Steps
          </Typography>

          <List>
            <ListItem>
              <ListItemText 
                primary="1. Install Tampermonkey"
                secondary="Click the link above and install from the Chrome Web Store. You must then enable the 'Allow User Scripts' option of the extension."
              />
            </ListItem>
            <ListItem>
              <ListItemText 
                primary="2. Install the Karaoke Queue Script"
                secondary="Click the button below. Tampermonkey will prompt you to install the script."
              />
            </ListItem>
            <ListItem>
              <ListItemText 
                primary="3. Install Transpose (Recommended)"
                secondary="Click the link above to install the Transpose extension"
              />
            </ListItem>
            <ListItem>
              <ListItemText 
                primary="4. Go to YouTube"
                secondary="Open any YouTube video. You should see the login panel and skip button."
              />
            </ListItem>
          </List>
        </Paper>
      </Box>

      {/* Install Button - Stays fixed at bottom */}
      <Box sx={{ textAlign: "center", mt: 3 }}>
        <Button
          variant="contained"
          size="large"
          onClick={handleInstallScript}
          endIcon={<OpenInNewIcon />}
          sx={{ py: 1.5, px: 5, fontSize: "1.1rem" }}
        >
          Install / Update Karaoke Queue Script
        </Button>
      </Box>

      <Typography 
        variant="body2" 
        color="text.secondary" 
        align="center" 
        sx={{ mt: 3 }}
      >
        After installing both extensions, just open YouTube.<br />
        The script will show a login panel and a big skip button.
      </Typography>
    </Box>
  );
}